<template>
  <div id="blockDetails">
    <!-- Block Details -->
    <b-card
      id="blockDetailsCard"
      class="blockDrawer large"
      :class="$store.state.drawer.blockDetails.position"
    >
      <template v-slot:header>
        <a
          class="close clickable"
          @click="hideBlockDetails()"
        >
          <i class="far fa-times"></i>
        </a>
        <!-- Block Title -->
        <h4 id="blockTitle">{{ $store.state.block.name }}</h4>
        <!-- Block Settings Button -->
        <!-- <b-btn
          v-if="$store.state.role == 'Administrator' || $store.state.role == 'Editor'"
          @click="editBlockSettings()"
          id="blockSettings"
          class="mb-2"
        >
          <i class="fas fa-cog"></i> Block Settings
        </b-btn> -->
        <b-btn
          v-if="$store.state.role == 'Administrator' || $store.state.role == 'Editor'"
          @click="editBlockSettings()"
          id="blockSettings"
          class="mb-2"
        >
          <i class="fas fa-pencil"></i> Edit Block
        </b-btn>
        <!-- REVISIONS -->
        <b-btn
          v-if="$store.state.role == 'Administrator' || $store.state.role == 'Editor'"
          @click="viewRevisions()"
          class="mb-2 ml-1"
        >
          <i class="fas fa-code-commit"></i> Revisions
        </b-btn>
      </template>
      <p>{{ $store.state.block.description }}</p>
      <p><strong>ID:</strong> {{ $store.state.block.id }}</p>
      <p v-if="$store.state.userProfile.measurementSystem === 'Metric'"><strong>Area:</strong> {{ $store.state.block.area | toHectares }} hectares</p>
      <p v-if="$store.state.userProfile.measurementSystem === 'Imperial'"><strong>Area:</strong> {{ $store.state.block.area | toAcres }} acres</p>
      <div v-if="$store.state.farm.blockFields && $store.state.farm.blockFields.length > 0">
        <div v-for="(groupedFields, group) in groupedBlockFields" v-bind:key="group">
          <h5 class="text-underline" v-if="group !== 'Others'">{{ group }}</h5> <!-- Group Title -->
          <!-- FIELDS -->
          <div
            v-for="(field, i) in groupedFields"
            v-bind:key="i"
          >
            <div v-if="!field.hidden">
              <!-- TEXT FIELDS -->
              <p
                v-if="field.type !== 'Image' && field.type !== 'Date and Time' && !field.isLogEnabled"
              ><strong>{{ field.label }}:</strong> {{ $store.state.block[field.machine_name] }} <span v-if="$store.state.farm.blockFields[i].suffix">{{ $store.state.farm.blockFields[i].suffix }}</span></p>
              <div v-if="field.isLogEnabled" class="mb-2">
                <p class="mb-1">
                  <strong>{{ field.label }}:</strong> {{ getLatestLogDisplay(field) }} <span v-if="$store.state.farm.blockFields[i].suffix">{{ $store.state.farm.blockFields[i].suffix }}</span>
                  <b-button size="sm" variant="outline-secondary" class="ml-2" @click="openLogHistory(field)">View logs</b-button>
                </p>
              </div>
              <!-- DATE AND TIME FIELDS -->
              <div v-if="field.type === 'Date and Time' && $store.state.block[field.machine_name] && !field.isLogEnabled">
                <p
                  v-if="field.type === 'Date and Time' && field.includeTime && $store.state.block[field.machine_name]"
                ><strong>{{ field.label }}:</strong> {{ $store.state.block[field.machine_name] | dateTime }}</p>
                <p
                  v-if="field.type === 'Date and Time' && !field.includeTime && $store.state.block[field.machine_name]"
                ><strong>{{ field.label }}:</strong> {{ $store.state.block[field.machine_name] | dateTimeDate }}</p>
              </div>
              <!-- IMAGE GALLERY -->
              <p
                v-if="field.type === 'Image' && slideshowsReady && !field.isLogEnabled"
              ><strong>{{ field.label }}:</strong> Image(s)</p>
              <b-carousel
                v-if="field.type === 'Image' && slideshowsReady"
                :id="field.machine_name"
                controls
                indicators
                class="mb-2"
              >
                <b-carousel-slide
                  v-for="(image, i) in slideshows[field.machine_name].slides"
                  v-bind:key="i"
                  :img-src="image.url"
                >
                  <div v-if="image.caption" class="image-caption-overlay text-center small">
                    {{ image.caption }}
                  </div>
                </b-carousel-slide>
              </b-carousel>

              <!-- Fullscreen viewer for non-logged image headers (matches log history viewer style) -->
              <b-btn
                v-if="field.type === 'Image' && !field.isLogEnabled && slideshowsReady"
                size="sm"
                variant="outline-secondary"
                class="mb-3"
                @click="openFullscreen(field)"
              >
                View full screen
              </b-btn>
            </div><!-- /IF NOT HIDDEN -->
          </div><!-- /FIELDS -->
        </div><!-- /GROUPS -->
        <p v-if="$store.state.block.updatedOn != undefined"><strong>Updated:</strong> {{ $store.state.block.updatedOn | dateTime }}</p>
        <p v-if="$store.state.block.createdOn != undefined"><strong>Created:</strong> {{ $store.state.block.createdOn | dateTime }}</p>
      </div><!-- if dynamic -->
    </b-card>

    <LogHistoryModal
      :show="showLogModal"
      entity-type="block"
      :entity-id="$store.state.block && $store.state.block.id"
      :farm-id="$store.state.farm && $store.state.farm.id"
      :header="logHistoryHeader"
      :can-edit="$store.state.role === 'Administrator' || $store.state.role === 'Editor'"
      @updated="onLogUpdated"
      @close="showLogModal = false"
    />

    <!-- Shared fullscreen image viewer for standard (non-logged) headers -->
    <b-modal
      v-model="fullscreenSlides"
      size="xl"
      body-class="p-0"
      content-class="log-image-viewer-modal"
      hide-header
      hide-footer
      @hidden="fullscreenIndex = 0"
    >
      <div class="d-flex flex-column h-100" style="min-height: 70vh;">
        <div class="d-flex align-items-center justify-content-between p-2 border-bottom bg-light">
          <span class="small text-muted">
            <span v-if="fullscreenFieldLabel">{{ fullscreenFieldLabel }} — </span>
            Image {{ fullscreenIndex + 1 }} of {{ fullscreenImages.length }}
          </span>
          <div>
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-1"
              :disabled="isFirstFullscreenImage"
              @click="prevFullscreenImage"
            >
              <i class="fas fa-chevron-left"></i> Previous
            </b-button>
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-1"
              :disabled="isLastFullscreenImage"
              @click="nextFullscreenImage"
            >
              Next <i class="fas fa-chevron-right"></i>
            </b-button>
            <b-button
              size="sm"
              variant="success"
              class="mr-1"
              :disabled="!currentFullscreenImageUrl"
              @click="downloadFullscreenImage"
            >
              <i class="fas fa-download"></i> Download
            </b-button>
            <b-button size="sm" variant="secondary" @click="fullscreenSlides = false">Close</b-button>
          </div>
        </div>
        <div class="flex-grow-1 d-flex align-items-center justify-content-center p-3 bg-dark overflow-auto">
          <div class="text-center">
            <img
              v-if="currentFullscreenImageUrl"
              :src="currentFullscreenImageUrl"
              class="log-viewer-img"
              alt="Image"
            />
            <span v-else class="text-white">Loading image…</span>
            <div v-if="currentFullscreenCaption" class="mt-2 small text-light">
              {{ currentFullscreenCaption }}
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div><!-- /#blockDetails -->
</template>

<script>
import firebase from 'firebase/compat/app'
import moment from 'moment'
import { getBlockLogHistory } from '../../services/HeaderLogService'
import LogHistoryModal from '../LogHistoryModal'
const fb = require('../../firebaseConfig.js')

export default {
  components: { LogHistoryModal },
  data () {
    return {
      slideshows: {},
      slideshowsReady: false,
      fullscreenSlides: false,
      fullscreenImages: [],
      fullscreenIndex: 0,
      fullscreenFieldLabel: '',
      logHistory: {},
      showLogModal: false,
      logHistoryHeader: null
    }
  },
  created () {
    this.getImageUrls()
    this.loadLogHistory()
  },
  computed: {
    groupedBlockFields () {
      return this.$store.state.farm.blockFields.reduce((grouped, field) => {
        const group = field.group || 'Others'
        if (!grouped[group]) {
          grouped[group] = []
        }
        grouped[group].push(field)
        return grouped
      }, {})
    },
    currentFullscreenImage () {
      if (!this.fullscreenImages || !this.fullscreenImages.length) return null
      const idx = Math.min(Math.max(this.fullscreenIndex, 0), this.fullscreenImages.length - 1)
      return this.fullscreenImages[idx]
    },
    currentFullscreenImageUrl () {
      const img = this.currentFullscreenImage
      return img && img.url ? img.url : null
    },
    currentFullscreenCaption () {
      const img = this.currentFullscreenImage
      return img && img.caption ? img.caption : ''
    },
    isFirstFullscreenImage () {
      return this.fullscreenIndex <= 0
    },
    isLastFullscreenImage () {
      return !this.fullscreenImages || this.fullscreenIndex >= this.fullscreenImages.length - 1
    }
  },
  methods: {
    viewRevisions () {
      this.$store.commit('hideDrawer', 'blockDetails')
      setTimeout(() => {
        this.$store.commit('showDrawer', 'blockRevisions')
      }, 300)
    },
    openFullscreen (field) {
      if (!field || !field.machine_name || !this.slideshows[field.machine_name]) return
      const slides = this.slideshows[field.machine_name].slides || []
      this.fullscreenImages = slides
      this.fullscreenIndex = 0
      this.fullscreenFieldLabel = field.label || ''
      this.fullscreenSlides = true
    },
    prevFullscreenImage () {
      if (this.isFirstFullscreenImage) return
      this.fullscreenIndex -= 1
    },
    nextFullscreenImage () {
      if (this.isLastFullscreenImage) return
      this.fullscreenIndex += 1
    },
    downloadFullscreenImage () {
      const img = this.currentFullscreenImage
      if (!img) return
      this.downloadImage(img)
    },
    downloadImage (image) {
      let path = null
      if (image && (image.large || image.path)) {
        path = image.large || image.path
      } else if (typeof image === 'string') {
        path = image
      }
      if (!path || typeof path !== 'string') return

      firebase.storage().ref(path).getDownloadURL().then(async url => {
        const idx = path.lastIndexOf('/')
        const filename = idx >= 0 ? path.substring(idx + 1) : path
        try {
          const res = await fetch(url, { mode: 'cors' })
          const blob = await res.blob()
          const a = document.createElement('a')
          a.href = URL.createObjectURL(blob)
          a.download = filename || 'image.jpg'
          a.rel = 'noopener'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(a.href)
        } catch (e) {
          window.open(url, '_blank', 'noopener')
        }
      }).catch(err => {
        console.error('Download failed:', err)
      })
    },
    getImageUrls () {
      if (!this.$store.state.farm.blockFields || !this.$store.state.block) return
      this.$store.state.farm.blockFields.forEach(field => {
        if (field.type !== 'Image') return
        const raw = this.$store.state.block[field.machine_name]
        if (!raw) return
        const images = Array.isArray(raw) ? raw : [raw]
        this.slideshows[field.machine_name] = {
          slides: [],
          show: false
        }
        let completed = 0
        images.forEach((image, index) => {
          const path = image.large || image.path
          if (!path) return
          firebase.storage().ref(path).getDownloadURL().then(url => {
            this.$set(this.slideshows[field.machine_name].slides, index, {
              path: image.path || path,
              url: url,
              caption: image.caption
            })
            completed++
            if (completed === images.length) {
              this.slideshows[field.machine_name].show = true
              let readyToShowSlideshows = true
              this.$store.state.farm.blockFields.forEach(f => {
                if (f.type === 'Image' && !this.slideshows[f.machine_name].show) {
                  readyToShowSlideshows = false
                }
              })
              this.slideshowsReady = readyToShowSlideshows
            }
          })
        })
      })
    },
    async loadLogHistory () {
      if (!this.$store.state.block || !this.$store.state.block.id) return
      const logHeaders = (this.$store.state.farm.blockFields || []).filter(field => field.isLogEnabled)
      await Promise.all(logHeaders.map(async field => {
        const { entries } = await getBlockLogHistory({
          blockId: this.$store.state.block.id,
          headerMachineName: field.machine_name,
          limit: 10
        })
        this.$set(this.logHistory, field.machine_name, entries)
      }))
    },
    getHeaderHistory (field) {
      return this.logHistory[field.machine_name] || []
    },
    getLatestLogDisplay (field) {
      if (!field || !field.machine_name) return '—'
      const latest = this.$store.state.block?.latestLogs && this.$store.state.block.latestLogs[field.machine_name]
      if (!latest || (latest.value === undefined || latest.value === null)) return '—'
      const v = latest.value
      if ((field.type === 'Date and Time' || field.logValueType === 'datetime') && v && typeof v.toDate === 'function') {
        const d = v.toDate()
        return field.includeTime ? d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : d.toLocaleDateString(undefined, { dateStyle: 'short' })
      }
      if ((field.type === 'Image' || field.logValueType === 'image') && v && typeof v === 'object') return 'Image(s)'
      return v
    },
    openLogHistory (field) {
      this.logHistoryHeader = field
      this.showLogModal = true
    },
    async onLogUpdated () {
      await this.loadLogHistory()
      if (this.$store.state.block && this.$store.state.block.id) {
        const doc = await fb.blocksCollection.doc(this.$store.state.block.id).get()
        if (doc.exists) {
          const blockData = { ...doc.data(), id: doc.id }
          this.$store.commit('setStateProperty', { property: 'block', value: blockData })
          this.$store.dispatch('refreshBlocksVizFromBlock', {
            blockId: this.$store.state.block.id,
            blockData
          })
          // Rebuild image slideshows so the drawer shows the new image
          this.slideshowsReady = false
          this.getImageUrls()
        }
      }
      if (this.$store.state.farm && this.$store.state.farm.id) {
        await fb.farmsCollection.doc(this.$store.state.farm.id).update({ recompileBlocks: true })
      }
    },
    formatLogHistoryEntry (entry) {
      const value = entry && entry.value !== undefined ? entry.value : '-'
      const note = entry && entry.notes ? ` (${entry.notes})` : ''
      if (!entry || !entry.timestamp || !entry.timestamp.seconds) return `${value}${note}`
      const date = entry.timestamp.toDate()
      return `${moment(date).format('MMM D, YYYY h:mm a')}: ${value}${note}`
    },
    hideBlockDetails () {
      // Turn all block layers Purple
      // this.$store.state.blocks.forEach(block => {
      //   const thisLayerId = 'Footprint-' + block.id
      //   this.$store.state.mapObject.setPaintProperty(thisLayerId, 'fill-color', '#6e59c7')
      // })
      if (this.$store.state.mapObject.getLayer('blockHighlightLayer')) {
        this.$store.state.mapObject.removeLayer('blockHighlightLayer')
      }
      if (this.$store.state.mapObject.getSource('blockHighlightLayer')) {
        this.$store.state.mapObject.removeSource('blockHighlightLayer')
      }
      // Hide the details
      this.$store.commit('hideDrawer', 'blockDetails')
      // this.$store.commit('setDrawer', { name: 'blockDetails', position: 'out' })
    },
    // editBlockSettings () {
    //   this.$store.commit('setDrawer', { name: 'blockDetails', position: 'out' })
    //   this.$store.commit('setEditMode', true)
    //   const self = this
    //   setTimeout(function () {
    //     self.$store.commit('toggleModal', 'createBlock')
    //   }, 300)
    // },
    editBlockSettings () {
      // this.$store.commit('setDrawer', { name: 'blockDetails', position: 'out' })
      this.$store.commit('hideDrawer', 'blockDetails')
      this.$store.commit('setEditMode', true)
      const self = this
      setTimeout(function () {
        // self.$store.commit('toggleModal', 'createBlock')
        // self.$store.commit('setStateProperty', { property: 'showCreateBlockDrawer', value: true })
        // self.$store.commit('setDrawer', { name: 'createBlockDrawer', position: 'in' })
        self.$store.commit('showDrawer', 'createBlockDrawer')
      }, 300)
    }
  },
  filters: {
    toAcres (val) {
      const acres = val * 0.000247105
      const rounded = acres.toFixed(2)
      return rounded
    },
    toHectares (val) {
      const hectares = val / 10000
      const rounded = hectares.toFixed(2)
      return rounded
    },
    dateTime (val) {
      if (val === {} || !val.seconds) { return '-' }
      const date = val.toDate()
      return moment(date).fromNow()
      // return moment(date).format('MMMM Do YYYY, h:mm a')
    },
    dateTimeDate (val) {
      if (val === {} || !val.seconds) { return '-' }
      const date = val.toDate()
      // return moment(date).fromNow()
      return moment(date).format('MMMM Do, YYYY')
    }
  }
}

</script>
<style>
.log-viewer-img {
  max-width: 100%;
  max-height: 75vh;
  object-fit: contain;
}

.image-caption-overlay {
  display: inline-block;
  margin-top: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
}

.fullscreenSlideshow {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 16px;
  z-index: 1050;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.fullscreenSlideshow .carousel {
  max-height: 80vh;
}

.fullscreen-slide-inner {
  min-height: 60vh;
}

.fullscreenSlideshow .carousel-item img {
  max-height: 70vh;
  width: auto;
  object-fit: contain;
}
</style>
